import React from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';

import makeStyles from '@mui/styles/makeStyles';
import {
	Table as MuiTable,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableSortLabel,
	TableRow,
	Typography,
} from '@mui/material';

import { flexRender } from '@tanstack/react-table';
const useStyles = makeStyles((theme) => ({
	tableContainer: {
		width: '100%',
		height: '100%',
		position: 'relative',
	},
	table: {
		border: '1px solid rgba(0,0,0,0.1)',
		borderRadius: 4,
		width: '100%',
		height: '100%',
		// borderCollapse: 'collapse',
	},
	tableHeadCell: {
		fontWeight: 700,
		fontSize: '1.15em',
		padding: theme.spacing(1, 1),
		whiteSpace: 'nowrap',
	},
	noCursor: {
		cursor: 'unset',
	},
	tableBodyCell: {
		padding: theme.spacing(0.5, 1),
	},
	stickyHeaderCell: {
		backgroundColor: '#cbedfd', // theme.palette.primary.light,
	},
	noData: {
		textAlign: 'center',
		paddingTop: theme.spacing(3),
	},
	resizer: {
		position: 'absolute',
		right: 0,
		top: 0,
		height: '100%',
		width: 5,
		background: 'rgba(0, 0, 0, 0.5)',
		cursor: 'col-resize',
		userSelect: 'none',
		touchAction: 'none',
		opacity: 0,
		'&.isResizing': {
			background: 'blue',
			opacity: 1,
		},
		'&:hover': {
			opacity: 1,
		},
	},
}));

const ReactTableV8MUIView = ({
	className,
	isStickyHeader,
	showBorder,
	rowHighlightPicker,
	enableResizer,

	// table props
	// getTableProps,
	headerGroups,
	// prepareRow,
	rows,
	noDataMsg,
}) => {
	const classes = useStyles();
	return (
		<TableContainer className={cx(classes.tableContainer, { [className]: Boolean(className) })}>
			<MuiTable
				className={classes.table}
				stickyHeader={isStickyHeader}
				// {...getTableProps()}
				{...(showBorder ? { border: 1 } : {})}
			>
				{/** Table Header */}
				<TableHead>
					{headerGroups.map((headerGroup, headerGroupIdx) => (
						<TableRow
							key={`rTable-headerRow-${headerGroupIdx}`}
							// {...headerGroup.getHeaderGroupProps()}
						>
							{headerGroup.headers.map((header, columnInHeaderGroupIdx) => {
								const canSort = header.column.getCanSort();
								const isSorted = header.column.getIsSorted(); // its value is actually: false, asc, desc
								let canFilter = false;
								if (header.column.getCanFilter() && header.column.columnDef.filterComp) {
									canFilter = true;
								}
								return (
									<TableCell
										key={`rTable-headerCell-${columnInHeaderGroupIdx}`}
										className={cx(classes.tableHeadCell, {
											[classes.stickyHeaderCell]: isStickyHeader,
										})}
										colSpan={header.colSpan}
										{...(header.column.getCanSort()
											? { sortDirection: header.column.getIsSorted() }
											: {})}
										style={{ width: header.getSize() }}
										// ...(header.getCanSort()?{sortDirection:header.getIsSorted()}:{})
										// sortDirection
										// {...column.getHeaderProps()}
										// {...(column.canSort ? column.getSortByToggleProps() : {})}
									>
										<TableSortLabel
											active={Boolean(isSorted)}
											direction={isSorted ? isSorted : undefined}
											hideSortIcon={!canSort}
											onClick={header.column.getToggleSortingHandler()}
											className={cx({ [classes.noCursor]: !canSort })}
											// IconComponent={SortingIcon}
											sx={{
												width: '100%',
											}}
										>
											{flexRender(header.column.columnDef.header, header.getContext())}
										</TableSortLabel>
										{enableResizer && header.column.getCanResize() && (
											<div
												onMouseDown={header.getResizeHandler()}
												onTouchStart={header.getResizeHandler()}
												className={cx(classes.resizer, {
													[classes.isResizing]: header.column.getIsResizing(),
												})}
											></div>
										)}
										{canFilter &&
											flexRender(header.column.columnDef.filterComp, {
												column: header.column,
											})}
									</TableCell>
								);
							})}
						</TableRow>
					))}
				</TableHead>
				{/** Table Body */}
				{rows.length > 0 ? (
					<TableBody>
						{/* rows is "An array of materialized row objects from the original data array and columns passed into the table options" */}
						{rows.map((row, rowInPageIdx) => {
							// prepareRow(row);
							let isRowHighlight = rowHighlightPicker && rowHighlightPicker(row);
							return (
								<TableRow
									key={`rTable-rowInPage-${rowInPageIdx}`}
									// {...row.getRowProps()}
									hover={true}
									selected={isRowHighlight}
								>
									{row.getVisibleCells().map((cell, cellInRowIdx) => {
										return (
											<TableCell
												key={`rTable-cellInRow-${cellInRowIdx}`}
												className={cx(classes.tableBodyCell, {
													// [classes.inheritHighlight]: isRowHighlight,
												})}
												// padding="none"
												// size="small"
												// {...cell.getCellProps()}
											>
												{/* {cell.render('Cell')} */}
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</TableCell>
										);
									})}
								</TableRow>
							);
						})}
					</TableBody>
				) : (
					<TableBody>
						<TableRow>
							<TableCell colSpan="100%">
								<Typography className={classes.noData} color="error" variant="h4">
									{noDataMsg}
								</Typography>
							</TableCell>
						</TableRow>
					</TableBody>
				)}
			</MuiTable>
		</TableContainer>
	);
};

ReactTableV8MUIView.propTypes = {
	/**
	 * className applys to table container
	 */
	className: PropTypes.string,
	/**
	 * if true, the header of table is sticky
	 */
	isStickyHeader: PropTypes.bool,
	enableResizer: PropTypes.bool,
	showBorder: PropTypes.bool,

	/**
	 * function to pick up the rows that will be highlight (e.g. row is selected)
	 * NB: if this function is not provided, row highlight is disabled
	 * @param {object} row. It is the row object returned by the table instance
	 * @returns {bool} if true, highlight the row, otherwise don't
	 *
	 * Example: (row) => row.isSelected
	 */
	rowHighlightPicker: PropTypes.func,

	// table props
	// getTableProps: PropTypes.func.isRequired,
	headerGroups: PropTypes.array.isRequired,
	// prepareRow: PropTypes.func.isRequired,
	rows: PropTypes.array.isRequired,

	// other props
	noDataMsg: PropTypes.string,
};

ReactTableV8MUIView.defaultProps = {
	isStickyHeader: false,
	showBorder: false,
	enableResizer: false,
	noDataMsg: 'No Data!!',
};
/**
 * Notes when using this component:
 * - The "height" of the parent is required to make the position & scrolling working properly
 */

export default ReactTableV8MUIView;
